/**
 * Client-side replay recorder: captures seed, per-frame dt + pointer, sparse keys/clicks.
 */

import {
  encodeDts,
  encodePointers,
  quantizeDtMs,
  sumDtMs,
} from "../../../lib/replay/codec";
import {
  REPLAY_FORMAT_VERSION,
  type ReplayClickEvent,
  type ReplayDevice,
  type ReplayKeyEvent,
  type ReplayPayloadV1,
} from "../../../lib/replay/types";

export type ReplayRecorderOptions = {
  mode: string;
  metric: string;
  seed: number;
  canvasW: number;
  canvasH: number;
  gameVersion: string;
  device: ReplayDevice;
  level?: number;
  presetId?: string;
};

export class ReplayRecorder {
  private readonly opts: ReplayRecorderOptions;
  private readonly dtMs: number[] = [];
  private readonly pointers: Array<{ x: number; y: number }> = [];
  private readonly keys: ReplayKeyEvent[] = [];
  private readonly clicks: ReplayClickEvent[] = [];
  private active = false;
  private finalized: ReplayPayloadV1 | null = null;

  constructor(opts: ReplayRecorderOptions) {
    this.opts = { ...opts };
  }

  start(): void {
    this.active = true;
    this.finalized = null;
    this.dtMs.length = 0;
    this.pointers.length = 0;
    this.keys.length = 0;
    this.clicks.length = 0;
  }

  stop(): void {
    this.active = false;
  }

  get isActive(): boolean {
    return this.active;
  }

  /** Call once per simulation frame after advanceGameSimClock. */
  recordFrame(dtSec: number, cursorX: number, cursorY: number, canvasW: number, canvasH: number): void {
    if (!this.active) return;
    const w = canvasW > 0 ? canvasW : this.opts.canvasW;
    const h = canvasH > 0 ? canvasH : this.opts.canvasH;
    // Keep payload canvas size aligned with the size used to normalize pointers.
    if (w > 0) this.opts.canvasW = Math.floor(w);
    if (h > 0) this.opts.canvasH = Math.floor(h);
    this.dtMs.push(quantizeDtMs(dtSec));
    this.pointers.push({
      x: w > 0 ? Math.max(0, Math.min(1, cursorX / w)) : 0.5,
      y: h > 0 ? Math.max(0, Math.min(1, cursorY / h)) : 0.5,
    });
  }

  recordKey(simMs: number, key: string, down: boolean): void {
    if (!this.active) return;
    const k = String(key || "").toLowerCase();
    if (!k || k.length > 32) return;
    this.keys.push({ t: Math.max(0, Math.floor(simMs)), key: k, down: !!down });
  }

  recordClick(simMs: number, x: number, y: number, canvasW: number, canvasH: number, button = 0): void {
    if (!this.active) return;
    const w = canvasW > 0 ? canvasW : 1;
    const h = canvasH > 0 ? canvasH : 1;
    this.clicks.push({
      t: Math.max(0, Math.floor(simMs)),
      x: Math.max(0, Math.min(1, x / w)),
      y: Math.max(0, Math.min(1, y / h)),
      button,
    });
  }

  finalize(value: number): ReplayPayloadV1 | null {
    this.active = false;
    if (this.dtMs.length < 6) {
      this.finalized = null;
      return null;
    }
    const durationMs = sumDtMs(this.dtMs);
    const payload: ReplayPayloadV1 = {
      v: REPLAY_FORMAT_VERSION,
      gameVersion: this.opts.gameVersion,
      mode: this.opts.mode,
      metric: this.opts.metric,
      seed: this.opts.seed >>> 0 || 1,
      canvasW: Math.floor(this.opts.canvasW) || 800,
      canvasH: Math.floor(this.opts.canvasH) || 600,
      durationMs,
      value: Math.floor(value),
      device: this.opts.device,
      dtsB64: encodeDts(this.dtMs),
      ptrB64: encodePointers(this.pointers),
    };
    if (this.opts.level != null) payload.level = this.opts.level;
    if (this.opts.presetId) payload.presetId = this.opts.presetId;
    if (this.keys.length) payload.keys = this.keys.slice();
    if (this.clicks.length) payload.clicks = this.clicks.slice();
    this.finalized = payload;
    return payload;
  }

  getFinalized(): ReplayPayloadV1 | null {
    return this.finalized;
  }
}
